import { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Descriptions, Image, Row, Space, Tag, Typography } from 'antd'
import { EnvironmentOutlined, FileTextOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { LocationPicker } from './LocationPicker'
import { useMapFallback } from './profileForm'
import {
  attachmentUrl,
  googleMapsLink,
  missingProfileFields,
  type AttachmentKind,
  type SubscriberProfile,
} from './profileApi'

interface SubscriberProfileCardProps {
  subscriberId: string
  profile: SubscriberProfile | null | undefined
}

const MISSING_LABEL: Record<string, string> = {
  firstName: 'subscriber.profile.firstName',
  fatherName: 'subscriber.profile.fatherName',
  lastName: 'subscriber.profile.lastName',
  deviceTypeId: 'subscriber.profile.deviceType',
  deviceOwnership: 'subscriber.profile.deviceOwnership',
  customerType: 'subscriber.profile.customerType',
  whatsapp: 'subscriber.profile.whatsapp',
  photoFileId: 'subscriber.profile.photo',
  contractFileId: 'subscriber.profile.contract',
  installedByName: 'subscriber.profile.installedBy',
  latitude: 'subscriber.profile.location',
}

export function SubscriberProfileCard({ subscriberId, profile }: SubscriberProfileCardProps) {
  const { t } = useTranslation()
  const fallback = useMapFallback()
  const [photo, setPhoto] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opening, setOpening] = useState(false)

  const photoId = profile?.photoFileId ?? null
  useEffect(() => {
    setPhoto(null)
    if (!photoId) return
    let alive = true
    attachmentUrl(subscriberId, 'PHOTO')
      .then((url) => alive && setPhoto(url))
      .catch((err) => alive && setError(translateError(err, t)))
    return () => {
      alive = false
    }
  }, [subscriberId, photoId, t])

  const open = async (kind: AttachmentKind) => {
    setOpening(true)
    setError(null)
    try {
      window.open(await attachmentUrl(subscriberId, kind), '_blank', 'noopener')
    } catch (err) {
      setError(translateError(err, t))
    } finally {
      setOpening(false)
    }
  }

  const missing = missingProfileFields(profile)
  const dash = t('common.dash')
  const location =
    profile?.latitude != null && profile.longitude != null
      ? { lat: profile.latitude, lng: profile.longitude }
      : null
  const fullName = [profile?.firstName, profile?.fatherName, profile?.lastName]
    .filter(Boolean)
    .join(' ')

  return (
    <Card title={t('subscriber.profile.section')}>
      {missing.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('subscriber.profile.incomplete')}
          description={missing.map((k) => t(MISSING_LABEL[k] ?? k)).join(', ')}
        />
      ) : null}
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('subscriber.profile.customerId')}>
              <Typography.Text copyable={Boolean(profile?.customerId)} strong>
                {profile?.customerId ?? dash}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.fullName')}>
              {fullName || dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.customerType')}>
              {profile?.customerType ? (
                <Tag color={profile.customerType === 'VIP' ? 'gold' : undefined}>
                  {t(`subscriber.customerType.${profile.customerType}`)}
                </Tag>
              ) : (
                dash
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.deviceType')}>
              {profile?.deviceTypeName ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.deviceOwnership')}>
              {profile?.deviceOwnership ? t(`subscriber.deviceOwnership.${profile.deviceOwnership}`) : dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.whatsapp')}>
              {profile?.whatsapp ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.installedBy')}>
              {profile?.installedByName ?? dash}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscriber.profile.contract')}>
              {profile?.contractFileId ? (
                <Button
                  size="small"
                  icon={<FileTextOutlined />}
                  loading={opening}
                  onClick={() => void open('CONTRACT')}
                >
                  {t('subscriber.profile.openContract')}
                </Button>
              ) : (
                dash
              )}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} lg={10}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {photoId ? (
              photo ? (
                <Image
                  src={photo}
                  alt={t('subscriber.profile.photo')}
                  style={{ maxHeight: 220, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
              )
            ) : null}
            {location ? (
              <>
                <LocationPicker value={location} fallbackCenter={fallback} readOnly height={220} />
                <Space wrap>
                  <Typography.Text type="secondary" copyable>
                    {`${location.lat}, ${location.lng}`}
                  </Typography.Text>
                  <Button
                    size="small"
                    icon={<EnvironmentOutlined />}
                    href={googleMapsLink(location.lat, location.lng)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('subscriber.profile.navigate')}
                  </Button>
                </Space>
              </>
            ) : null}
          </Space>
        </Col>
      </Row>
    </Card>
  )
}
